import { alpha } from '@mui/material';
import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';

type ReviewUpdatedZipCodesDialogProps = AsyncDialogProps<{ updateErrors: string[] }> & {
  companyId: string;
  initialZipCodesServiced: string[];
  selectedUnassignedStZips: string[];
  selectedServicedZips: string[];
};

const ReviewUpdatedZipCodesDialog = (props: ReviewUpdatedZipCodesDialogProps) => {
  const {
    onClose,
    companyId,
    initialZipCodesServiced,
    selectedUnassignedStZips,
    selectedServicedZips,
    ...dialogProps
  } = props;

  const api = useContext(ApiContext);
  const companyZonesPutMutation = api.companies.useUpdateServicedZipCodes();

  const zipCodesServiced = selectedUnassignedStZips.concat(selectedServicedZips);

  const zipCodesAdded = zipCodesServiced.filter((zip) => !initialZipCodesServiced.includes(zip));
  const zipCodesRemoved = initialZipCodesServiced.filter((zip) => !selectedServicedZips.includes(zip));

  const handleConfirm = async () => {
    try {
      await companyZonesPutMutation.mutateAsync({
        id: companyId,
        data: { zipCodesServiced: zipCodesServiced }
      });
      onClose();
    } catch (err) {
      if (err instanceof BadRequestError) {
        if (err.responseData?.nonFieldErrors) {
          return onClose({ action: 'CONFIRM', value: { updateErrors: err.responseData.nonFieldErrors } });
        }
        if (err.responseData.fieldIssues) {
          const invalidZipCodes = (err.responseData.fieldIssues || []).flatMap((o) => {
            if (o.path && o.path[0] === 'zipCodesServiced' && typeof o.path[1] === 'number') {
              const zipIndex = o.path[1];
              return [`Invalid zip code: ${zipCodesServiced[zipIndex]}. ${o.message}`];
            }
            return [];
          });
          if (invalidZipCodes.length) {
            return onClose({ action: 'CONFIRM', value: { updateErrors: invalidZipCodes } });
          }
        }

        return onClose({
          action: 'CONFIRM',
          value: { updateErrors: ['Unexpected errors occurred saving Serviced Zip Codes'] }
        });
      }
    }
  };

  return (
    <DialogWrapper dismissable={true} onClose={onClose} titleText='Review Changes' {...dialogProps}>
      <DialogContent sx={{ display: 'flex', minWidth: '450px', minHeight: '240px' }}>
        <Stack spacing={2.5} padding={2} useFlexGap sx={{ width: '100%' }}>
          {zipCodesAdded.length > 0 && (
            <HeaderBox headerText='Zip Codes Added'>
              <Typography
                sx={(theme) => ({ backgroundColor: alpha(theme.palette.success.main, 0.1) })}
                text={zipCodesAdded.join(', ')}
              />
            </HeaderBox>
          )}
          {zipCodesRemoved.length > 0 && (
            <HeaderBox headerText='Zip Codes Removed'>
              <Typography
                sx={(theme) => ({ backgroundColor: alpha(theme.palette.error.main, 0.1) })}
                text={zipCodesRemoved.join(', ')}
              />
            </HeaderBox>
          )}
          {zipCodesAdded.length === 0 && zipCodesRemoved.length === 0 && <Typography text='No Changes Detected' />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton color='cancel' onClick={() => onClose({ action: 'CANCEL' })} text='Cancel' />
        <DialogButton
          onClick={handleConfirm}
          text='Save'
          disabled={zipCodesAdded.length === 0 && zipCodesRemoved.length === 0}
        />
      </DialogActions>
    </DialogWrapper>
  );
};

interface ReviewUpdatedZipCodesDialog
  extends AsyncDialog<{ updateErrors: string[] }, ReviewUpdatedZipCodesDialogProps> {}
export const useReviewUpdatedZipCodesDialog = (): ReviewUpdatedZipCodesDialog =>
  useAsyncDialog(ReviewUpdatedZipCodesDialog);
